/**
 * useProductDetail — TanStack Query v5 data layer for the product-detail page
 * (demo slice). Calls the composed `GET /marketplace/products/{id}` endpoint
 * (product + seller + market + images + category names) in ONE round-trip.
 *
 * Hand-written local interfaces mirror the backend `PublicProductDetailResponse`
 * Pydantic shape (the no-codegen pattern `useMarketplaceBrowse.ts` follows;
 * `packages/shared-types` is a stub). Pydantic v2 serializes `Decimal` as a JSON
 * STRING (verified: `price_kgs` arrives as `"1500.00"`, `lat` as `"42.9011"`),
 * so prices + coordinates are typed `string | number | null` here and consumers
 * coerce with `Number(...)` at the point of use.
 *
 * The endpoint is PUBLIC (unauthenticated GET); a Bearer header is attached by
 * the api-client when present but ignored server-side.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiClient } from "@/lib/api-client";

/** One product image, as a ready-to-use URL composed server-side. */
export interface ProductImage {
  url: string;
}

/** Seller block (mirrors backend `PublicSellerResponse`). */
export interface ProductDetailSeller {
  id: string;
  business_name: string;
  market_id: string;
  pavilion_number: string;
  /**
   * NULL when the API withheld it (v0053): where two stalls shared a number
   * the importer minted a sentinel for the second claimant, and the server
   * refuses to serve those. 9 of 79 QA sellers are in that state and every one
   * reads as `verified`, so this being nullable is the only signal there is.
   */
  phone_e164: string | null;
  entrance_hint: string | null;
  whatsapp_phone_e164: string | null;
  telegram_handle: string | null;
  lat: string | number | null;
  lng: string | number | null;
  pavilion_photo_s3_key: string | null;
  /** A0 trust signals. `verified` is always true on the public surface (a
   * product is only reachable via a verified seller). `rating` is a Decimal
   * string ("4.8") or null; `warranty` drives the "Гарантия" pill. */
  verified: boolean;
  rating: string | number | null;
  reviews_count: number;
  warranty: boolean;
}

/** Market block (mirrors backend `PublicMarketResponse`). */
export interface ProductDetailMarket {
  id: string;
  slug: string;
  name_ru: string;
  name_ky: string | null;
  city: string;
  address: string;
  lat: string | number;
  lng: string | number;
}

/** One RU/KY/EN triple on an attribute (mirrors backend `TrilingualText`);
 * `ru` is always present, `ky`/`en` fall back to it. */
export interface AttributeTrilingual {
  ru: string;
  ky: string | null;
  en: string | null;
}

/** One stored attribute value on the public detail (mirrors backend
 * `AttributeValueOut`). Per locked Q4 the RAW `value` (+ raw `unit`) is what is
 * displayed; the backend-derived `value_std` is never shown. A `select` value
 * is the option slug — its human label is `option_label`. */
export interface ProductAttribute {
  field_slug: string;
  field_type: string;
  value: string | number | boolean | null;
  unit: string | null;
  value_std: string | number | null;
  label: AttributeTrilingual;
  option_label: AttributeTrilingual | null;
}

/** Composed product-detail payload (mirrors `PublicProductDetailResponse`). */
export interface ProductDetail {
  id: string;
  seller_id: string;
  category_id: string;
  name: string;
  description: string | null;
  price_kgs: string | number | null;
  bulk_price_kgs: string | number | null;
  bulk_threshold: number | null;
  stock_state: string;
  category_name_ru: string | null;
  category_name_ky: string | null;
  seller: ProductDetailSeller;
  market: ProductDetailMarket;
  images: ProductImage[];
  /** PIM Wave P2 — the public detail always returns attribute values (`[]`
   * when none); typed optional so older cached shapes stay assignable. The
   * consumer guards with `?? []`. Rendered as a spec table (raw value + raw
   * unit, Q4). */
  attributes?: ProductAttribute[];
  /** v0048 product properties. All optional so an older cached shape stays
   * assignable; every consumer guards. */
  unit?: string | null;
  brand?: string | null;
  /** Published only when its provenance permits — the API withholds an
   * article reconstructed from an ad title. */
  article?: string | null;
  formal_name?: string | null;
  /** Upper end of the price span: the dearest priced SKU, or the top of a
   * range the workbook stated. `null` means the price is exact. */
  price_max_kgs?: string | number | null;
  /** `exact` | `range` | `tiered` (v0052) — a `tiered` band is a vendor
   *  ladder keyed to pack size, so its ends are real but the values
   *  between them are not a continuum. Optional: an older API build
   *  omits it, and an absent kind simply renders no caveat. */
  price_kind?: string | null;
  variant_count?: number;
  variants?: ProductVariant[];
  /** The seller's own words, per register. */
  tags?: string[];
  folk_names?: string[];
}

/** One purchasable SKU behind a product. */
export interface ProductVariant {
  id: string;
  /** The dimension the SKUs vary along — «Размер», «Модель», «АКБ». */
  variant_attribute_key: string;
  /** This SKU's label — «20x3,4», «Гайковерт 350». */
  variant_attribute_value: string;
  article: string | null;
  price_kgs: string | number | null;
  stock_qty: number | null;
  sort_order: number;
}

export function productDetailQueryKey(id: string): readonly unknown[] {
  return ["marketplace", "product", id];
}

export function useProductDetail(id: string): UseQueryResult<ProductDetail> {
  return useQuery({
    queryKey: productDetailQueryKey(id),
    queryFn: async (): Promise<ProductDetail> =>
      apiClient.get<ProductDetail>(`/marketplace/products/${id}`),
    staleTime: 30_000,
  });
}
